<script setup lang="ts">
import {
  IForm,
  IFormError,
  IFormGroup,
  IFormLabel,
  IInput,
  useForm,
} from '@inkline/inkline'
import FormConsent from './FormConsent.vue'
import Icon from '~/components/global/Icon.vue'
import { useAPIRequest } from '~/composables/api/useAPIRequest'
import type { EstateDetailInterface } from '~/types/api/EstateDetail'
import trackGtmEvent from '~/utils/tracking/tackGtmEvent'
import getFormTrackingData from '~/utils/tracking/getFormTrackingData'
import type useCustomOfferModalStore from '~/stores/modals/useCustomOfferModal'

const props = defineProps<{
  modalStore: ReturnType<typeof useCustomOfferModalStore>
  estateData: EstateDetailInterface
}>()

const { executeRecaptcha } = useGoogleRecaptcha()
const { t } = useI18n()
const { form, schema, validate } = useForm({
  name: {
    validators: [
      {
        name: 'required',
        message: t('forms.customOffer.fields.name.requiredMessage'),
      },
    ],
  },
  phone: {
    validators: [
      {
        name: 'custom',
        message: t('forms.customOffer.fields.phone.invalidMessage'),
        validator: (v: string) => !v || /^\+?[0-9 ]+$/.test(v),
      },
    ],
  },
  email: {
    validators: [
      {
        name: 'required',
        message: t('forms.customOffer.fields.email.requiredMessage'),
      },
      {
        name: 'email',
        message: t('forms.customOffer.fields.email.invalidMessage'),
      },
    ],
  },
})

function setValidatorToShowErrors() {
  let isFirst = true
  for (const schemaFieldKey of Object.keys(schema.value)) {
    const schemaField = schema.value[schemaFieldKey]

    if (schemaField?.constructor !== Object || schemaField?.valid) {
      continue
    }

    if (isFirst) {
      ;(
        document.querySelector(`[name="${schemaFieldKey}"]`) as HTMLInputElement
      )?.focus()

      isFirst = false
    }

    schemaField.value = schemaField.value ?? ''
    schemaField.pristine = false
    schemaField.dirty = true
  }
}

function resetForm() {
  for (const schemaFieldKey of Object.keys(schema.value)) {
    const schemaField = schema.value[schemaFieldKey]

    if (schemaField?.constructor !== Object) {
      continue
    }

    delete schemaField.value
    schemaField.pristine = true
    schemaField.dirty = false
  }
}

const isSubmitting = ref(false)
async function onSubmit() {
  isSubmitting.value = true

  await validate()

  if (!schema.value.valid) {
    setValidatorToShowErrors()

    isSubmitting.value = false

    return false
  }

  const serviceLines = {
    office: 'Office Agency',
    warehouse: 'Industrial Agency',
    retail: 'Retail Agency',
  }

  const { token } = await executeRecaptcha('custom_offer')

  useAPIRequest('/contact/send', {
    method: 'POST',
    body: {
      ...form.value,
      service_line: serviceLines[props.estateData.type],
      type: 'Property detail - Tailor Made Offer',
      url: window.location.origin + window.location.pathname,
      token,
    },
  })
    .then((response) => {
      if (!response.status) {
        showToast({
          title: t('forms.customOffer.errorTitle'),
          message: response.error || response.message,
          color: 'danger',
        })

        return
      }

      showToast({
        title: t('forms.customOffer.successTitle'),
        message: t('forms.customOffer.successMessage'),
      })

      trackGtmEvent('odoslanie_formularu', {
        form_type: 'ponuka-na-mieru',
        ...getFormTrackingData(),
      })

      resetForm()
      props.modalStore.close()
    })
    .catch((error) => {
      showToast({
        title: t('forms.customOffer.errorTitle'),
        message: error.message,
        color: 'danger',
      })
    })
    .finally(() => {
      isSubmitting.value = false
    })
}

watch(
  () => props.modalStore.isOpened,
  (isOpened) => {
    if (!isOpened) return

    trackGtmEvent('videnie_formularu', {
      form_type: 'ponuka-na-mieru',
      ...getFormTrackingData(),
    })
  }
)
</script>

<template>
  <IForm
    v-model="schema"
    novalidate
    class="c-custom-offer-form has-floating-labels"
    @submit="onSubmit"
  >
    <IFormGroup>
      <IFormLabel>
        <span class="sc-label"
          >{{ $t('forms.customOffer.fields.name.label') }}
          <span class="sc-required">*</span>
        </span>
      </IFormLabel>
      <IInput name="name" placeholder="" />
      <IFormError for="name" />
    </IFormGroup>

    <IFormGroup>
      <IFormLabel>
        <span class="sc-label"
          >{{ $t('forms.customOffer.fields.email.label') }}
          <span class="sc-required">*</span>
        </span>
      </IFormLabel>
      <IInput name="email" type="email" placeholder="" />
      <IFormError for="email" />
    </IFormGroup>

    <IFormGroup>
      <IFormLabel>
        <span class="sc-label"
          >{{ $t('forms.arrangeVisit.fields.phone.label') }}
        </span>
      </IFormLabel>
      <IInput name="phone" type="tel" placeholder="" />
      <IFormError for="phone" />
    </IFormGroup>

    <IFormGroup>
      <IButton
        class="has-icon"
        color="secondary"
        type="submit"
        size="lg"
        block
        :loading="isSubmitting"
        :show-loading-icon="true"
        @click="onSubmit"
      >
        {{ $t('forms.customOffer.submit') }}

        <Icon name="arrow-right-thick" />
      </IButton>
    </IFormGroup>

    <IFormGroup>
      <FormConsent class="c-custom-offer-form__consent" />
    </IFormGroup>
  </IForm>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-custom-offer-form {
  &__consent {
    font-size: 15px;
    line-height: 1.28;
    font-weight: 400;
    color: var(--color-text-dark-2);
  }
}
</style>
